package com.swetdot.swetgram.ui.shop

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.swetdot.swetgram.domain.XpShopCategoryDomain
import com.swetdot.swetgram.domain.XpShopItemDomain
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetConfirmDialog
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.shop_balance_title
import swetgram.shared.generated.resources.shop_category_icons
import swetgram.shared.generated.resources.shop_category_other
import swetgram.shared.generated.resources.shop_category_perks
import swetgram.shared.generated.resources.shop_category_profile
import swetgram.shared.generated.resources.shop_category_streaks
import swetgram.shared.generated.resources.shop_confirm_body
import swetgram.shared.generated.resources.shop_confirm_no
import swetgram.shared.generated.resources.shop_confirm_title
import swetgram.shared.generated.resources.shop_confirm_yes
import swetgram.shared.generated.resources.shop_empty
import swetgram.shared.generated.resources.shop_freezes_badge
import swetgram.shared.generated.resources.shop_freezes_tooltip
import swetgram.shared.generated.resources.shop_purchased
import swetgram.shared.generated.resources.shop_spendable_balance
import swetgram.shared.generated.resources.shop_title

@Composable
fun XpShopScreen(
    state: XpShopUiState,
    onBack: () -> Unit,
    onAskPurchase: (XpShopItemDomain) -> Unit,
    onConfirmPurchase: () -> Unit,
    onDismissConfirm: () -> Unit,
    onDismissError: () -> Unit,
    onDismissNotice: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors

    if (state.confirmingItem != null) {
        SwetConfirmDialog(
            title = stringResource(Res.string.shop_confirm_title),
            body = stringResource(
                Res.string.shop_confirm_body,
                state.confirmingItem.priceXp,
                state.confirmingItem.title,
            ),
            confirmText = stringResource(Res.string.shop_confirm_yes),
            cancelText = stringResource(Res.string.shop_confirm_no),
            onConfirm = onConfirmPurchase,
            onDismiss = onDismissConfirm,
            destructive = false,
        )
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(colors.backgroundDeep)
            .statusBarsPadding(),
    ) {
        SwetTopAppBar(
            title = stringResource(Res.string.shop_title),
            onBack = onBack,
            backContentDescription = "Back",
        )

        // Error banner
        state.error?.let { err ->
            SwetErrorBanner(
                message = err.resolve(),
                onDismiss = onDismissError,
            )
        }

        // Notice banner
        state.notice?.let { notice ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(colors.accent.copy(alpha = 0.12f))
                    .padding(start = Space.two, end = Space.half, top = Space.one, bottom = Space.one),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SwetIcon(
                    icon = SwetIcons.Check,
                    tint = colors.accent,
                    contentDescription = null,
                    size = Sizes.iconSmall,
                )
                Spacer(Modifier.width(Space.one))
                Text(
                    text = notice.resolve(),
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.textPrimary,
                    modifier = Modifier.weight(1f),
                )
                SwetIconButton(
                    contentDescription = "Dismiss",
                    onClick = onDismissNotice,
                ) { tint ->
                    SwetIcon(
                        icon = SwetIcons.Close,
                        contentDescription = null,
                        tint = tint,
                        size = Sizes.iconSmall,
                    )
                }
            }
        }

        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .navigationBarsPadding(),
            contentPadding = PaddingValues(
                horizontal = Space.screenMargin,
                vertical = Space.two,
            ),
            verticalArrangement = Arrangement.spacedBy(Space.two),
        ) {
            // Balance Card
            item(key = "balance_card") {
                ShopBalanceCard(
                    spendableXp = state.spendableXp,
                    streakFreezes = state.streakFreezes,
                )
            }

            if (state.loading && state.items.isEmpty()) {
                item(key = "loading") {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 48.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        CircularProgressIndicator(
                            color = colors.accent,
                            strokeWidth = 3.dp,
                        )
                    }
                }
            } else if (state.items.isEmpty()) {
                item(key = "empty") {
                    Text(
                        text = stringResource(Res.string.shop_empty),
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.textSecondary,
                        modifier = Modifier.padding(vertical = Space.two),
                    )
                }
            } else {
                val grouped = state.items.groupBy { it.category }
                grouped.forEach { (category, itemsInCategory) ->
                    item(key = "header_${category.name}") {
                        Text(
                            text = categoryTitle(category),
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = colors.textPrimary,
                            modifier = Modifier.padding(top = Space.one, bottom = Space.half),
                        )
                    }

                    items(itemsInCategory, key = { it.code }) { item ->
                        ShopItemCard(
                            item = item,
                            canAfford = state.spendableXp >= item.priceXp,
                            purchasing = state.purchasingCode == item.code,
                            onPurchase = { onAskPurchase(item) },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ShopBalanceCard(
    spendableXp: Int,
    streakFreezes: Int,
) {
    val colors = SwetgramThemeTokens.colors

    Surface(
        color = colors.surface,
        shape = RoundedCornerShape(Radius.lg),
        border = BorderStroke(Sizes.borderHairline, colors.border),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(Modifier.padding(Space.two)) {
            Text(
                text = stringResource(Res.string.shop_balance_title),
                style = MaterialTheme.typography.labelMedium,
                color = colors.textSecondary,
            )
            Spacer(Modifier.height(Space.half))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = stringResource(Res.string.shop_spendable_balance, spendableXp),
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = colors.accent,
                )

                Surface(
                    color = colors.graphite,
                    shape = RoundedCornerShape(Radius.full),
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = Space.one, vertical = Space.half),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = "❄️ " + stringResource(Res.string.shop_freezes_badge, streakFreezes),
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Medium,
                            color = colors.textPrimary,
                        )
                    }
                }
            }
            Spacer(Modifier.height(Space.half))
            Text(
                text = stringResource(Res.string.shop_freezes_tooltip),
                style = MaterialTheme.typography.bodySmall,
                color = colors.textTertiary,
            )
        }
    }
}

@Composable
private fun ShopItemCard(
    item: XpShopItemDomain,
    canAfford: Boolean,
    purchasing: Boolean,
    onPurchase: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Surface(
        color = colors.surface,
        shape = RoundedCornerShape(Radius.md),
        border = BorderStroke(Sizes.borderHairline, colors.border),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(
            modifier = Modifier.padding(Space.two),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(44.dp)
                    .clip(CircleShape)
                    .background(colors.graphite),
                contentAlignment = Alignment.Center,
            ) {
                val icon = when (item.category) {
                    XpShopCategoryDomain.STREAK_SAVER -> SwetIcons.Steps
                    XpShopCategoryDomain.APP_ICON -> SwetIcons.ImageIcon
                    XpShopCategoryDomain.AVATAR_FRAME -> SwetIcons.Flame
                    XpShopCategoryDomain.BADGE -> SwetIcons.Shield
                    XpShopCategoryDomain.BLACK_PASS -> SwetIcons.Crystal
                    XpShopCategoryDomain.REACTION_PACK -> SwetIcons.Reaction
                    XpShopCategoryDomain.OTHER -> SwetIcons.Check
                }
                SwetIcon(
                    icon = icon,
                    tint = colors.accent,
                    contentDescription = null,
                    size = Sizes.iconMedium,
                )
            }

            Spacer(Modifier.width(Space.two))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = item.title,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = colors.textPrimary,
                )
                Spacer(Modifier.height(2.dp))
                Text(
                    text = item.description,
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.textSecondary,
                )
            }

            Spacer(Modifier.width(Space.one))

            if (item.isOwned) {
                Surface(
                    color = colors.graphite,
                    shape = RoundedCornerShape(Radius.full),
                ) {
                    Text(
                        text = stringResource(Res.string.shop_purchased),
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Medium,
                        color = colors.textSecondary,
                        modifier = Modifier.padding(horizontal = Space.one, vertical = Space.half),
                    )
                }
            } else {
                Button(
                    onClick = onPurchase,
                    enabled = canAfford && !purchasing && item.enabled,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = colors.accent,
                        contentColor = colors.backgroundDeep,
                        disabledContainerColor = colors.graphite,
                        disabledContentColor = colors.textTertiary,
                    ),
                    shape = RoundedCornerShape(Radius.full),
                    contentPadding = PaddingValues(horizontal = Space.one, vertical = Space.half),
                ) {
                    if (purchasing) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            strokeWidth = 2.dp,
                            color = colors.backgroundDeep,
                        )
                    } else {
                        Text(
                            text = "${item.priceXp} XP",
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun categoryTitle(category: XpShopCategoryDomain): String = when (category) {
    XpShopCategoryDomain.STREAK_SAVER -> stringResource(Res.string.shop_category_streaks)
    XpShopCategoryDomain.APP_ICON -> stringResource(Res.string.shop_category_icons)
    XpShopCategoryDomain.AVATAR_FRAME, XpShopCategoryDomain.BADGE -> stringResource(Res.string.shop_category_profile)
    XpShopCategoryDomain.BLACK_PASS, XpShopCategoryDomain.REACTION_PACK -> stringResource(Res.string.shop_category_perks)
    XpShopCategoryDomain.OTHER -> stringResource(Res.string.shop_category_other)
}
